import React, { useEffect, useState } from 'react';
import { Select } from 'components';
import { useDebounce } from 'hooks/useDebounce';

const DebouncedSelect = ({
  value,
  intialSelectedOption,
  onChange,
  placeholder,
  fetchOptions,
  limit = 10,
  minLength = 3,
  id,
  disabled = false,
  isClearable = true,
  formatLabel = (item) => item.name,
}) => {
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 500);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getOptions = async () => {
      if (debouncedInput.length < minLength) {
        setOptions([]);
        return;
      }

      setIsLoading(true);
      try {
        const res = await (await fetchOptions({ name: debouncedInput, limit })).json();
        if (res) {
          const options = res.results.result.map((item) => ({
            label: formatLabel(item),
            value: item.id,
          }));
          setOptions(options);
        }
      } catch (err) {
        console.error('Failed to fetch options', err);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    getOptions();
  }, [debouncedInput, limit, minLength]);

  const handleOnChange = (option) => {
    setSelectedOption(option);
    onChange(option);
  };

  useEffect(() => {
    if (intialSelectedOption) setSelectedOption(intialSelectedOption);
  }, [intialSelectedOption]);

  const hasSelectedOption = selectedOption && Object.keys(selectedOption).length > 0;
  const combinedOptions = hasSelectedOption ? [...options, selectedOption] : options;

  const uniqueOptions = combinedOptions.filter(
    (option, idx, arr) => arr.findIndex((o) => o.value === option.value) === idx
  );

  return (
    <Select
      id={id}
      value={value}
      placeholder={selectedOption?.label ?? placeholder}
      onChange={handleOnChange}
      options={uniqueOptions}
      onInputChange={(val) => setInput(val)}
      isLoading={isLoading}
      disableFiltering
      disabled={disabled}
      isClearable={isClearable}
    />
  );
};

export default DebouncedSelect;
